import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';
import Badge from './Badge';
import { getAllBadges, getBadgesByType, hasBadge, getRarityColor, BADGE_TYPES } from '../utils/badgeDefinitions';
import './BadgeModal.css';

const BadgeModal = ({ isOpen, onClose, userBadges = [], userProgress = {} }) => {
  if (!isOpen) return null;
  
  const allBadges = getAllBadges();
  const earnedBadgeIds = (userBadges || []).map(b => {
    if (typeof b === 'string') return b;
    return b.id || b;
  });
  
  // Group badges by type
  const badgesByType = {
    [BADGE_TYPES.STREAK]: getBadgesByType(BADGE_TYPES.STREAK),
    [BADGE_TYPES.LEVEL]: getBadgesByType(BADGE_TYPES.LEVEL),
    [BADGE_TYPES.LESSON]: getBadgesByType(BADGE_TYPES.LESSON),
    [BADGE_TYPES.QUIZ]: getBadgesByType(BADGE_TYPES.QUIZ),
    [BADGE_TYPES.XP]: getBadgesByType(BADGE_TYPES.XP),
    [BADGE_TYPES.FACT]: getBadgesByType(BADGE_TYPES.FACT),
    [BADGE_TYPES.PERFECT]: getBadgesByType(BADGE_TYPES.PERFECT),
    [BADGE_TYPES.MASTERY]: getBadgesByType(BADGE_TYPES.MASTERY)
  };
  
  const typeLabels = {
    [BADGE_TYPES.STREAK]: '🔥 Streak Badges',
    [BADGE_TYPES.LEVEL]: '⭐ Level Badges',
    [BADGE_TYPES.LESSON]: '📚 Lesson Badges',
    [BADGE_TYPES.QUIZ]: '📝 Quiz Badges',
    [BADGE_TYPES.XP]: '💰 XP Badges',
    [BADGE_TYPES.FACT]: '💡 Fact Badges',
    [BADGE_TYPES.PERFECT]: '💯 Perfect Score',
    [BADGE_TYPES.MASTERY]: '🎯 Mastery Badges'
  };
  
  const earnedCount = earnedBadgeIds.length;
  const totalCount = allBadges.length;
  const progressPercentage = (earnedCount / totalCount) * 100;
  
  return (
    <AnimatePresence>
      <motion.div
        className="badge-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="badge-modal-content"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="badge-modal-header">
            <h2 className="badge-modal-title">Badge Collection</h2>
            <button className="badge-modal-close" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
          
          <div className="badge-modal-progress">
            <div className="progress-info">
              <span className="progress-text">
                {earnedCount} / {totalCount} Badges Earned
              </span>
              <span className="progress-percentage">
                {Math.round(progressPercentage)}%
              </span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
          
          <div className="badge-modal-body">
            {Object.entries(badgesByType).map(([type, badges]) => {
              if (badges.length === 0) return null;
              
              return (
                <div key={type} className="badge-category-section">
                  <h3 className="badge-category-title">{typeLabels[type]}</h3>
                  <div className="badge-grid">
                    {badges.map((badge) => {
                      const isEarned = hasBadge(userBadges, badge.id);
                      return (
                        <div
                          key={badge.id}
                          className={`badge-item-container ${isEarned ? 'earned' : 'locked'}`}
                        >
                          <Badge
                            badge={badge}
                            size="large"
                            showTooltip={false}
                          />
                          {!isEarned && (
                            <div className="badge-locked-overlay">
                              <span className="lock-icon">🔒</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BadgeModal;

